import type { ChecklistDto } from '@iac/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDict } from '../../i18n/dictionary';
import { api, unwrap } from '../../lib/api';
import {
  useCreateResource,
  useDeleteResource,
  useResourceQuery,
  useUpdateResource,
} from '../../lib/crud';
import { useInvalidating } from '../../lib/query';
import { checklistsDict } from './i18n';
import { affectedListIds } from './lists';

// One key for the whole app: the list holds every checklist whole, and every
// page reads its list, and a combined list its children, out of it. The groups
// sit under it, so any write that invalidates the lists refetches them too —
// the first list's new default group shows up without extra wiring.
const CHECKLISTS = ['checklists'] as const;
const GROUPS = [...CHECKLISTS, 'groups'] as const;

export function useChecklists() {
  const t = useDict(checklistsDict);
  return useResourceQuery(
    CHECKLISTS,
    () => api.checklists.listChecklists(),
    t.loadFailed,
  );
}

export function useCreateChecklist() {
  const t = useDict(checklistsDict);
  return useCreateResource(
    CHECKLISTS,
    api.checklists.createChecklist,
    t.createFailed,
    t.created,
  );
}

export function useUpdateChecklist() {
  const t = useDict(checklistsDict);
  return useUpdateResource(
    CHECKLISTS,
    api.checklists.updateChecklist,
    t.updateFailed,
    t.updated,
  );
}

export function useDeleteChecklist() {
  const t = useDict(checklistsDict);
  return useDeleteResource(
    CHECKLISTS,
    api.checklists.deleteChecklist,
    t.deleteFailed,
  );
}

/** In the user's order. */
export function useChecklistGroups() {
  const t = useDict(checklistsDict);
  return useResourceQuery(
    GROUPS,
    () => api.checklists.listChecklistGroups(),
    t.loadGroupsFailed,
  );
}

export function useCreateChecklistGroup() {
  const t = useDict(checklistsDict);
  return useCreateResource(
    CHECKLISTS,
    api.checklists.createChecklistGroup,
    t.addGroupFailed,
  );
}

export function useRenameChecklistGroup() {
  const t = useDict(checklistsDict);
  return useUpdateResource(
    CHECKLISTS,
    api.checklists.renameChecklistGroup,
    t.renameGroupFailed,
    t.groupSaved,
  );
}

export function useReorderChecklistGroups() {
  const t = useDict(checklistsDict);
  return useInvalidating(CHECKLISTS, async (ids: string[]) =>
    unwrap(
      await api.checklists.reorderChecklistGroups({ body: { ids } }),
      200,
      t.reorderFailed,
    ),
  );
}

/** Refetches the lists too, which come back in the new order. */
export function useReorderGroupLists() {
  const t = useDict(checklistsDict);
  return useInvalidating(
    CHECKLISTS,
    async (v: { groupId: string; ids: string[] }) =>
      unwrap(
        await api.checklists.reorderChecklistGroupLists({
          params: { id: v.groupId },
          body: { ids: v.ids },
        }),
        204,
        t.reorderFailed,
      ),
  );
}

export function useDeleteChecklistGroup() {
  const t = useDict(checklistsDict);
  return useDeleteResource(
    CHECKLISTS,
    api.checklists.deleteChecklistGroup,
    t.deleteGroupFailed,
  );
}

/** Every item of the lists in `listIds` set to `checked`, the rest as they were. */
function withChecked(
  lists: ChecklistDto[] | undefined,
  listIds: ReadonlySet<string>,
  checked: boolean,
  itemId?: string,
): ChecklistDto[] | undefined {
  return lists?.map((list) =>
    list.kind === 'standard' && listIds.has(list.id)
      ? {
          ...list,
          items: list.items.map((item) =>
            itemId === undefined || item.id === itemId
              ? { ...item, checked }
              : item,
          ),
        }
      : list,
  );
}

/**
 * One tick, shown at once. The cache already holds the answer, so a landed
 * tick refetches nothing — a refetch after every tap would flicker under
 * quick taps. A failed one puts that item back; the failure is toasted by the
 * mutation cache like any other.
 */
export function useSetItem() {
  const t = useDict(checklistsDict);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      listId: string;
      itemId: string;
      checked: boolean;
    }) =>
      unwrap(
        await api.checklists.setChecklistItem({
          params: { id: v.listId, itemId: v.itemId },
          body: { checked: v.checked },
        }),
        200,
        t.tickFailed,
      ),
    onMutate: async ({ listId, itemId, checked }) => {
      // A refetch already on its way would land the old tick over this one.
      await queryClient.cancelQueries({ queryKey: CHECKLISTS });
      queryClient.setQueryData<ChecklistDto[]>(CHECKLISTS, (lists) =>
        withChecked(lists, new Set([listId]), checked, itemId),
      );
    },
    // Only this item goes back: a tick made meanwhile on another item stays.
    onError: (_error, { listId, itemId, checked }) => {
      queryClient.setQueryData<ChecklistDto[]>(CHECKLISTS, (lists) =>
        withChecked(lists, new Set([listId]), !checked, itemId),
      );
    },
  });
}

/**
 * Unticks the list — for a combined list, every child — at once, then
 * refetches so the server's word is final. A failure restores what was ticked.
 */
export function useResetChecklist() {
  const t = useDict(checklistsDict);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (listId: string) =>
      unwrap(
        await api.checklists.resetChecklist({ params: { id: listId } }),
        204,
        t.resetFailed,
      ),
    onMutate: async (listId) => {
      await queryClient.cancelQueries({ queryKey: CHECKLISTS });
      const previous = queryClient.getQueryData<ChecklistDto[]>(CHECKLISTS);
      if (previous)
        queryClient.setQueryData(
          CHECKLISTS,
          withChecked(previous, affectedListIds(listId, previous), false),
        );
      return { previous };
    },
    onError: (_error, _listId, context) => {
      if (context?.previous)
        queryClient.setQueryData(CHECKLISTS, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: CHECKLISTS }),
  });
}
